import React, { useState, useEffect } from "react";
import SbuxDialog2 from "../../../../components/SbuxDialog2";
import SbuxTranslate from "../../../../components/SbuxTranslate";
import { Box, Button, DialogContent, DialogActions } from "@mui/material";
import styles from "../styles";
import useCss from "../../../../hooks/useCss";
import { dateAtTimezone } from "../../../../utils/dateUtil";
import { formatLastUpdated } from "../../../../utils/tableUtil";
import {
  DataManagementCustomInput,
  FieldData,
  FormEntry,
} from "./DataManagementCustomInput";
import { validateFormValues } from "../table/dataMgt/utils";
import { useAppSelector } from "../../../../store/types";
import { getTimezoneSelector } from "../../../../selectors/settingsSelector";
import {
  LAST_UPDATED_FIELD_ID,
  ISO_UTC_DATE_REGEX,
} from "../../../../constants";

interface ColumnDef {
  id?: string;
  label?: string;
  type?: string;
  fieldVisible?: boolean;
  uiPosition?: number;
  required?: boolean;
  inputMaxLength?: number;
  lockedForEdit?: boolean;
  partitionKey?: boolean;
  sortKey?: string | boolean;
  selectValues?: unknown;
  [key: string]: unknown;
}

interface RowData extends FieldData {
  value?: unknown;
  type?: string;
  uiPosition?: number;
  lockedForEdit?: boolean;
  sortKey?: string | boolean;
  isError?: boolean;
  errorMessage?: string;
}

interface Props {
  title: React.ReactNode;
  open: boolean;
  toggleDialogOpen: () => void;
  tableName: string | null;
  onSave: (payload: unknown) => void | Promise<void>;
  row?: Record<string, unknown> | null;
  columns: ColumnDef[];
  addNew: boolean;
  rows: Record<string, unknown>[];
  crudOperations?: string[];
}

const DataManagementDialog = ({
  title,
  open,
  toggleDialogOpen,
  tableName,
  onSave,
  row,
  columns,
  addNew,
  rows,
  crudOperations,
}: Props) => {
  const classes = useCss(styles);
  const [rowData, setRowData] = useState<RowData[]>([]);
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const tempFormValues = new Map<string, FormEntry>();
  const [formValues, setFormValues] = useState<Map<string, FormEntry>>(
    new Map(),
  );
  const timezone = useAppSelector(getTimezoneSelector);

  useEffect(() => {
    if (row && !addNew) {
      const initialData = Object.entries(row)
        .map(([label, value]) => {
          const item = columns.find((column) => column.id === label);

          if (!!item && item.fieldVisible) {
            const isEmpty = !value || String(value).trim().length === 0;
            const isIsoUtcDate =
              typeof value === "string" && ISO_UTC_DATE_REGEX.test(value);
            let displayValue: unknown;
            if (item.id === LAST_UPDATED_FIELD_ID) {
              displayValue = formatLastUpdated(
                {
                  lastUpdated: value as string,
                  lastUpdatedBy: row.lastUpdatedBy as string | null | undefined,
                },
                timezone,
              );
            } else if (
              (item.type === "datetime" ||
                item.type === "date" ||
                isIsoUtcDate) &&
              typeof value === "string" &&
              value
            ) {
              displayValue = dateAtTimezone(value, timezone);
            } else {
              displayValue = value;
            }
            tempFormValues.set(item.label ?? "", {
              ...item,
              value: displayValue != null ? String(displayValue) : undefined,
              isError: item.required && isEmpty,
            });

            return {
              label: item?.label,
              value: displayValue,
              type: item?.type,
              uiPosition: item?.uiPosition,
              required: item.required,
              inputMaxLength: item?.inputMaxLength,
              lockedForEdit: item?.lockedForEdit,
              partitionKey: item?.partitionKey,
              sortKey: item?.sortKey,
              name: item?.id,
              isError: item.required && isEmpty,
              selectValues: item?.selectValues as string[] | undefined,
            } as RowData;
          }
          return undefined;
        })
        .filter((data): data is RowData => !!data);

      setFormValues(tempFormValues);
      setRowData(
        initialData.sort((x, y) => (x.uiPosition ?? 0) - (y.uiPosition ?? 0)),
      );
      setIsSaveDisabled(true);
    } else {
      const emptyData = columns
        .filter(
          (column) =>
            column.fieldVisible &&
            !(column.id === LAST_UPDATED_FIELD_ID && column.lockedForEdit),
        )
        .map((column: ColumnDef) => {
          const isEmpty = true;
          const item: RowData = {
            label: typeof column === "string" ? column : column.label,
            value: "",
            type: column.type,
            uiPosition: column?.uiPosition,
            required: column?.required,
            inputMaxLength: column?.inputMaxLength,
            lockedForEdit: false,
            partitionKey: column?.partitionKey,
            sortKey: column.sortKey,
            name: column?.id,
            isError: column?.required && isEmpty,
            selectValues: column?.selectValues as string[] | undefined,
          };
          tempFormValues.set(item.label ?? "", {
            ...item,
            value: item.value != null ? String(item.value) : undefined,
            isError: column?.required && isEmpty,
          });
          return item;
        });
      setFormValues(tempFormValues);
      setRowData(emptyData);

      const hasError = validateFormValues(tempFormValues);
      setIsSaveDisabled(hasError);
    }
  }, [row, columns, timezone]);

  const handleInputChange = (
    data: { label?: string; required?: boolean; [key: string]: unknown },
    value: string,
    updatedFormValues: Map<string, FormEntry>,
  ) => {
    const controlValue = updatedFormValues.get(data.label ?? "");

    if (controlValue) {
      const isEmpty = !value || value.toString().trim().length === 0;
      updatedFormValues.set(data.label ?? "", {
        ...controlValue,
        value: value,
        isError: controlValue.required && isEmpty,
      });
    }

    setFormValues(updatedFormValues);
    setIsSaveDisabled(validateFormValues(updatedFormValues));
  };

  const handleOkayClick = async () => {
    if (validateFormValues(formValues)) {
      return;
    }

    const listDataPayload = {
      payload: {
        attributes: rowData.map((data) => {
          const item = columns.find((column) => column.label === data.label);
          const modifiedValue = item
            ? formValues.get(item.label ?? "")
            : undefined;
          return {
            name: item?.id,
            value: modifiedValue?.value,
          };
        }),
      },
      tableName: tableName,
      isEdit: !addNew,
    };
    onSave(listDataPayload);
  };

  const dialogConfig = {
    title,
  };
  const isFormDisable = !crudOperations?.includes("UPDATE") && !addNew;
  // first non-readonly, non-boolean field receives focus; all others suppress it
  const firstEditableIdx = rowData.findIndex((data) => {
    const isLocked = !!data?.lockedForEdit || isFormDisable;
    return !isLocked && data?.type !== "boolean";
  });
  return (
    <>
      <SbuxDialog2
        open={open}
        config={dialogConfig}
        handleOnClose={toggleDialogOpen}
      >
        <DialogContent dividers>
          <Box>
            <Box
              component="form"
              className={`${classes.formControl}`}
              noValidate
              autoComplete="off"
            >
              {rowData.map((data, idx) => {
                const isReadOnly = !!data?.lockedForEdit || isFormDisable;
                return (
                  <DataManagementCustomInput
                    key={idx}
                    rows={rows}
                    formValues={formValues}
                    data={data}
                    label={data?.label}
                    value={String(rowData[idx]?.value ?? "")}
                    handleInputChange={handleInputChange}
                    readOnly={isReadOnly}
                    type={data?.type}
                    required={data?.required}
                    preventAutoFocus={idx !== firstEditableIdx}
                  />
                );
              })}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions>
          <Box className="actionContainer">
            <Button
              onClick={handleOkayClick}
              variant="contained"
              size="medium"
              disabled={isSaveDisabled}
            >
              <SbuxTranslate>{"Save"}</SbuxTranslate>
            </Button>
            <Button variant="outlined" size="medium" onClick={toggleDialogOpen}>
              <SbuxTranslate>{"Cancel"}</SbuxTranslate>
            </Button>
          </Box>
        </DialogActions>
      </SbuxDialog2>
    </>
  );
};

export default DataManagementDialog;
